import BasicBox from '../../common/basic_box/basic_box.tsx';
import './driver_page.css';
import { useParams } from 'react-router';
import {
    Driver,
    DriverMetadata,
    DriverPackageWithDriver,
    ReleaseState,
} from '../../../types/driver_types.ts';
import { useQuery } from '@tanstack/react-query';
import Spinner from '../../common/spinner/spinner.tsx';
import { NavLink } from 'react-router';
import {
    ArduinoIconSVG,
    PythonIconSVG,
    RaspberryPiIconSVG,
    EmbeddedCIconSVG,
    CopyIcon,
    LinkIcon,
    DownloadIcon,
    GithubIcon,
    ArrowLeftIcon,
    EmbeddedCIconMonoSVG,
    PythonIconMonoSVG,
    ArduinoIconMonoSVG,
    RaspberryPiIconMonoSVG,
} from '../../../assets/icons.tsx';
import { formatDate, getLatestRelease } from '../../../utils/utils.ts';
import { ReleaseStatePill } from '../../common/release_state_pill/release_state_pill.tsx';
import { useAzureAuth } from '../../../hooks/use_azure_auth.ts';
import {
    fetchDriverDetails,
    fetchDriverPackage,
} from '../../../utils/di_api_functions.ts';

interface SwTargetSvgProps {
    swTargetName: string;
    isMonoColor?: boolean;
}

const SwTargetSvg = ({
    swTargetName,
    isMonoColor = false,
}: SwTargetSvgProps) => {
    if (swTargetName == 'Arduino') {
        if (isMonoColor) {
            return <ArduinoIconMonoSVG />;
        }
        return <ArduinoIconSVG />;
    }

    if (swTargetName == 'Python') {
        if (isMonoColor) {
            return <PythonIconMonoSVG />;
        }
        return <PythonIconSVG />;
    }

    if (swTargetName == 'Raspberry Pi') {
        if (isMonoColor) {
            return <RaspberryPiIconMonoSVG />;
        }
        return <RaspberryPiIconSVG />;
    }

    if (swTargetName == 'Embedded') {
        if (isMonoColor) {
            return <EmbeddedCIconMonoSVG />;
        }
        return <EmbeddedCIconSVG />;
    }

    return <span></span>;
};

const ErrorPlaceholder = (props: { error: Error }) => {
    return (
        <div className="driver_page__placeholder">
            <h4>Error</h4>
            {props.error.message}
        </div>
    );
};

const SpinnerPlaceholder = (props: { text: string }) => {
    return (
        <div className="driver_page__placeholder">
            <Spinner />
            <span>{props.text}</span>
        </div>
    );
};

const DriverBanner = ({ driver }: { driver: Driver }) => {
    const packageName = driver.driver_package.name;
    const interfaceName = driver.interface.name;
    const swTarget = driver.sw_target.name;

    const svgClass = `driver_page__banner_product_img ${swTarget.replace(/ /g, '')}`;

    return (
        <>
            <BasicBox className="driver_page__banner" flexDirection="row">
                <div className="driver_page__banner_product_description">
                    <div className="driver_page__banner_product">
                        {packageName}
                    </div>
                    <div className="driver_page__banner_interface">
                        {interfaceName}
                    </div>
                </div>
                <div className={svgClass}>
                    <SwTargetSvg swTargetName={swTarget} />
                </div>
            </BasicBox>
        </>
    );
};

const ReleaseStateText = ({ releaseState }: { releaseState: ReleaseState }) => {
    if (releaseState == 'PUB') {
        return <div>This driver can be shared without restriction</div>;
    } else if (releaseState == 'ITL') {
        return <div>This driver must not be shared outside of Sensirion</div>;
    } else if (releaseState == 'DPR') {
        return <div>This driver should not be used anymore</div>;
    } else if (releaseState == 'PRV') {
        return <div>This driver can be shared with lead customers</div>;
    } else {
        return <></>;
    }
};

const DriverRepository = ({ driver }: { driver: Driver }) => {
    const {
        userProps: { token: token },
    } = useAzureAuth();
    const showReleaseState = Boolean(token);
    const repositoryLink = driver.repository_link;
    const latestRelease = getLatestRelease(driver);
    const downloadLink = latestRelease?.download_link;

    return (
        <div className="driver_page__driver_repository_info">
            <BasicBox flexDirection="row">
                <div className="driver_page__driver_repository">
                    <div>
                        <GithubIcon size="4x" color="#f4f4f4" />
                    </div>
                    <div>Repository</div>
                    <div>{repositoryLink}</div>
                </div>
            </BasicBox>
            <div className="driver_page__driver_repository_actions">
                <BasicBox flexDirection="column">
                    <button
                        onClick={() => {
                            navigator.clipboard
                                .writeText(repositoryLink)
                                .catch(error =>
                                    console.error(
                                        'Failed to copy text: ',
                                        error,
                                    ),
                                );
                        }}
                        title="copy repository URL to clipboard"
                        className="driver_page__driver_repository_links__action"
                    >
                        <CopyIcon size="1x" />
                        <span className="driver_page__action_label">Copy</span>
                    </button>
                </BasicBox>
                <BasicBox flexDirection="column">
                    <a
                        href={repositoryLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="open repository"
                        className="driver_page__driver_repository_links__action"
                    >
                        <LinkIcon size="1x" />
                        <span className="driver_page__action_label">Visit</span>
                    </a>
                </BasicBox>
                <BasicBox flexDirection="column">
                    {downloadLink ? (
                        <a
                            href={downloadLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="download latest release as .zip"
                            className="driver_page__driver_repository_links__action"
                        >
                            <DownloadIcon size="1x" />
                            <span className="driver_page__action_label">
                                Download
                            </span>
                        </a>
                    ) : (
                        <div className="driver_page__driver_repository_links__action driver_page__driver_repository_links__action_disabled">
                            <DownloadIcon size="1x" color="#aaaaaa" />
                            <span className="driver_page__action_label">
                                Download
                            </span>
                        </div>
                    )}
                </BasicBox>
            </div>
            {showReleaseState && (
                <BasicBox flexDirection="row">
                    <div className="driver_page__driver_repository_state">
                        <ReleaseStatePill releaseState={driver.release_state} />
                        <ReleaseStateText releaseState={driver.release_state} />
                    </div>
                </BasicBox>
            )}
        </div>
    );
};

const DriverPageGeneralInformation = ({ driver }: { driver: Driver }) => {
    const latestRelease = getLatestRelease(driver);
    if (latestRelease) {
        latestRelease.release_date = formatDate(latestRelease.release_date);
    }

    return (
        <BasicBox>
            <div className="driver_page__details__title">
                General Information
            </div>
            <div className="driver_page__details__content">
                <span>Target</span>
                <span>{driver.sw_target?.name}</span>
                <span>Interface</span>
                <span>{driver.interface?.name}</span>
                <div className="driver_page__detail__separator"></div>
                <span>Latest version</span>
                <span>{latestRelease?.version || 'n/a'}</span>
                <span>Release date</span>
                <span>{latestRelease?.release_date || 'n/a'}</span>
            </div>
        </BasicBox>
    );
};

const DriverPageGeneratorMetadata = ({
    metadata,
}: {
    metadata?: DriverMetadata;
}) => {
    const title = 'Driver Generator Metadata';

    if (!metadata) {
        return (
            <BasicBox>
                <div className="driver_page__details__title">{title}</div>
                <div className="driver_page__details__content">
                    No metadata available
                </div>
            </BasicBox>
        );
    }

    return (
        <BasicBox>
            <div className="driver_page__details__title">{title}</div>
            <div className="driver_page__details__content">
                <span>Generator version</span>
                <span>{metadata.generator_version}</span>
                <span>Model version</span>
                <span>{metadata.model_version}</span>
                <span>First generated</span>
                <span>{formatDate(metadata.first_generated)}</span>
                <span>Last generated</span>
                <span>{formatDate(metadata.last_generated)}</span>
                <span>Manually modified</span>
                <span>{metadata.is_manually_modified ? 'yes' : 'no'}</span>
            </div>
        </BasicBox>
    );
};

interface DriverLinkPillProps {
    id: number;
    target: string;
    protocol: string;
}

const DriverLinkPill = ({ id, target, protocol }: DriverLinkPillProps) => {
    const svgClass = `driver_page__related_driver_svg ${target.replace(/ /g, '')}`;

    return (
        <NavLink to={`/driver/${id}`}>
            <div className="driver_page__related_driver">
                <div className={svgClass}>
                    <SwTargetSvg swTargetName={target} isMonoColor={true} />
                </div>
                <div className="driver_page__related_driver_target">
                    {target}
                </div>
                <div className="driver_page__related_driver__vertical_line"></div>
                <div className="driver_page__related_driver_protocol">
                    {protocol}
                </div>
            </div>
        </NavLink>
    );
};

const DriverPageRelatedDrivers = ({
    packageId,
    driverId,
}: {
    packageId: number;
    driverId: number;
}) => {
    const user = useAzureAuth();

    const { isPending, isError, data, error } = useQuery({
        queryKey: ['driverPackage', packageId, user.userProps.token],
        queryFn: () => fetchDriverPackage(packageId, user.userProps.token),
    });

    if (isPending) {
        return <SpinnerPlaceholder text="Loading driver package..." />;
    }

    if (isError) {
        return <ErrorPlaceholder error={error} />;
    }

    const driverPackage = data as DriverPackageWithDriver;
    const relatedDrivers = driverPackage.driver_set.filter(
        driver => driver.id !== driverId,
    );

    return (
        <BasicBox>
            <div className="driver_page__details__title">
                Other drivers for {driverPackage.name}
            </div>
            <div className="driver_page__related_driver_container">
                {relatedDrivers.map((driver, idx) => (
                    <DriverLinkPill
                        key={idx}
                        id={driver.id}
                        target={driver.sw_target.name}
                        protocol={driver.interface.name}
                    />
                ))}
            </div>
        </BasicBox>
    );
};

const DriverPage = () => {
    const params = useParams();
    const driverId = Number(params.driverId);

    const {
        userProps: { token: token },
    } = useAzureAuth();

    const { isPending, isError, data, error } = useQuery({
        queryKey: ['driver', driverId, token],
        queryFn: () => fetchDriverDetails(driverId, token),
    });

    if (isPending) {
        return <SpinnerPlaceholder text="Loading driver data..." />;
    }

    if (isError) {
        return <ErrorPlaceholder error={error} />;
    }

    const driverData = data as Driver;

    return (
        <>
            <NavLink to={'/'} className="driver_page_back_to_list">
                <ArrowLeftIcon size="1x" /> back to driver list
            </NavLink>
            <div className="driver_page__container">
                <DriverBanner driver={driverData} />
                <DriverRepository driver={driverData} />
                <div className="driver_page__details">
                    <DriverPageGeneralInformation driver={driverData} />
                    {token && (
                        <DriverPageGeneratorMetadata
                            metadata={driverData.metadata}
                        />
                    )}
                    {driverData.driver_package && (
                        <div
                            className={
                                token
                                    ? 'driver_page__related_drivers__full_width'
                                    : ''
                            }
                        >
                            <DriverPageRelatedDrivers
                                packageId={driverData.driver_package.id}
                                driverId={driverId}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default DriverPage;
